/*
 * @Author: Json Chen
 * @Date: 2021-06-16 16:35:31
 * @LastEditTime: 2022-05-27 17:46:21
 * @LastEditors: Json Chen
 * @Description:
 * @FilePath: /unifyxp-v2-store-portal/src/pages/Settings/LinkLibrary/Edit.tsx
 */

import { Radio, SectionCollapse } from '@/src/components/Antd';
import CommonHeader from '@/src/components/CommonHeader';
import WhiteBackgroundTooltip, {
  TooltipContainer,
  TooltipContent,
  TooltipTitle,
} from '@/src/components/GlobalTooltip/WhiteBackgroundTooltip';
import LinkItems from '@/src/pages/Settings/LinkLibrary/Components/LinkItems';
import MiniForm from '@/src/pages/Settings/LinkLibrary/Components/MiniForm';
import UrlForm from '@/src/pages/Settings/LinkLibrary/Components/UrlForm';
import useEditDetailByUuid from '@/src/pages/Settings/LinkLibrary/Hooks/useEditDetailByUuid';
import { getAllLanguage } from '@/src/request/common/index';
import { createNewLinkLibrary } from '@/src/request/settings/linkLibrary';
import {
  isUserValid,
  storeResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
  EPermissionSource,
} from '@/src/types/common/resources';
import { CaretDownOutlined, QuestionCircleFilled } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Form, Input, Select } from 'antd';
import React from 'react';
import { useHistory, useParams } from 'react-router';
import { breadcrumbData } from './config';
import styles from './style.module.less';
import { LinkType } from './type';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import { replaceI18nVariables } from '@/src/tools/tools';

const LinkLibraryEdit = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => {
    return i18nGet(i18n, str);
  };
  const linkTypeToText = {
    [LinkType.URL]: getI18n('storeLinkLibrary.detail.uRLLink'),
    [LinkType.MiniProgram]: getI18n('storeLinkLibrary.detail.miniProgramLink'),
  };
  const canCreate = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsLinkLibrary,
        operation: EnumOperation.create_edit_link,
      }),
    ],
  });
  const canDelete = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsLinkLibrary,
        operation: EnumOperation.delete_link,
      }),
    ],
  });
  const history = useHistory();
  const [form] = Form.useForm();

  const { uuid } = useParams<{ uuid: string | undefined }>();

  const {
    initTab,
    initTitle,
    initAppid,
    initPath,
    initUrl,
    initExtra,
    initLanguage,
    initRelatedMiniProgram,
  } = useEditDetailByUuid({ uuid });

  const [onSaveDisable, setOnSaveDisable] = React.useState<boolean>(true);
  // 获取所有的语言
  const { loading: languageLoading, data: languageOptionsList = [] } =
    useRequest(getAllLanguage);

  // 创建新的linkLibrary
  const { run: _createNewLinkLibrary } = useRequest(createNewLinkLibrary, {
    manual: true,
    onSuccess: (result) => {
      history.push(`/settings/linkLibrary`);
    },
  });

  React.useEffect(() => {
    // 判断是否是编辑,如果不是则是添加
    if (uuid) {
      form.setFieldsValue({
        linkType: initTab,
        title: initTitle,
        appid: initAppid,
        path: initPath,
        url: initUrl,
        extra: initExtra,
        language: initLanguage,
        relatedMiniProgram: initRelatedMiniProgram,
      });
    }
  }, [initTab, initTitle, initAppid, initUrl, initExtra, initLanguage]);

  const onValuesChange = () => {
    if (onSaveDisable) {
      setOnSaveDisable(!onSaveDisable);
    }
  };

  const onFormSubmit = () => {
    form.validateFields().then((values) => {
      const createNewFormData: any = {
        category: values.linkType,
        title: values.title,
        language: values.language,
      } as any;
      switch (values.linkType) {
        case LinkType.URL:
          createNewFormData.url = values.url;
          createNewFormData.relatedMiniProgram = values.relatedMiniProgram;
          break;
        case LinkType.MiniProgram:
          createNewFormData.appID = values.appid;
          createNewFormData.path = values.path;
          createNewFormData.extraData = values.extra;
          break;
      }
      if (uuid) {
        createNewFormData.uuid = uuid;
      }
      _createNewLinkLibrary(createNewFormData);
    });
  };
  const onGoBackClick = () => {
    history.push(`/settings/linkLibrary`);
  };
  return (
    <Form
      onValuesChange={onValuesChange}
      layout={'vertical'}
      form={form}
      className={styles.editForm}>
      <CommonHeader
        breadcrumbData={breadcrumbData({ getI18n })}
        onSaveDisable={!canCreate || onSaveDisable}
        onSubmit={onFormSubmit}
        onDiscard={onGoBackClick}
        onGoBackClick={onGoBackClick}
        disableDicard={!canDelete}
      />
      <div className={styles.commonContainer}>
        <Form.Item
          className={`${styles.linkTypeRadio} ${
            !uuid ? styles.show : styles.hide
          }`}
          name={['linkType']}
          label={
            <div className={styles.linkTypeLabel}>
              {getI18n('storeLinkLibrary.detail.linkType')}
            </div>
          }
          initialValue={LinkType.URL}>
          <Radio
            radioList={[
              { value: LinkType.URL, label: linkTypeToText[LinkType.URL] },
              {
                value: LinkType.MiniProgram,
                label: linkTypeToText[LinkType.MiniProgram],
              },
            ]}
          />
        </Form.Item>

        <div className={styles.linkTypeContent}>
          <Form.Item
            shouldUpdate={(prevValues, curValues) =>
              prevValues.linkType !== curValues.linkType
            }>
            {({ getFieldValue }) => {
              const linkType: LinkType = getFieldValue('linkType');
              return (
                <div className={styles.linkTypeName}>
                  {linkTypeToText[linkType]}
                </div>
              );
            }}
          </Form.Item>
          <div className={styles.linkTypeContentCommon}>
            <Form.Item
              name={['title']}
              label={`${getI18n('general.title')}*`}
              rules={[
                {
                  required: true,
                  pattern: /\S/,
                  message: replaceI18nVariables(
                    getI18n('general.fieldisrequired'),
                    getI18n('general.title'),
                  ),
                },
              ]}>
              <Input size='large' maxLength={200} />
            </Form.Item>
            <Form.Item
              name={['language']}
              label={getI18n('storeLinkLibrary.detail.destinationLanguage')}
              rules={[{ required: false }]}>
              <Select
                loading={languageLoading}
                showSearch
                size='large'
                placeholder={getI18n('general.pleaseselectavalue')}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                suffixIcon={<CaretDownOutlined className={styles.selectIcon} />}
                style={{
                  width: '100%',
                }}>
                {languageOptionsList.map((item) => (
                  <Select.Option key={item.uuid} value={item.languageTag}>
                    {item.description}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            shouldUpdate={(prevValues, curValues) =>
              prevValues.linkType !== curValues.linkType
            }>
            {({ getFieldValue }) => {
              const linkType: LinkType = getFieldValue('linkType');
              switch (linkType) {
                case LinkType.URL:
                  return <UrlForm />;
                case LinkType.MiniProgram:
                  return <MiniForm />;
              }
            }}
          </Form.Item>
        </div>

        {uuid && (
          <div className={styles.linkedItemsContainer}>
            <SectionCollapse
              open={true}
              id={'linkedItems'}
              titleStyle={{ height: '64px' }}
              title={
                <div className={styles.linkedItemsTitle}>
                  <span className={styles.linkedItemsTitleText}>
                    {getI18n('storeLinkLibrary.detail.linkedItems')}
                  </span>
                  <WhiteBackgroundTooltip
                    title={
                      <TooltipContainer>
                        <TooltipTitle>
                          {getI18n('storeLinkLibrary.detail.linkedItems')}
                        </TooltipTitle>
                        <TooltipContent>
                          {getI18n('storeLinkLibrary.detail.itemsTips')}
                        </TooltipContent>
                      </TooltipContainer>
                    }
                    placement='right' //overlayClassName
                  >
                    <QuestionCircleFilled />
                  </WhiteBackgroundTooltip>
                </div>
              }
              content={<LinkItems uuid={uuid} />}
              openMargin={16}
            />
          </div>
        )}
      </div>
    </Form>
  );
};

export default LinkLibraryEdit;
